import api from './api';
import authService from './authService';

const subscriptionService = {
  /**
   * Upgrade user's subscription to a paid plan
   * @param {string} plan - Plan slug ('standard')
   * @param {string} paymentMethod - Payment method ('credit_card' or 'paypal')
   * @param {object} paymentDetails - Payment details (simulated for now)
   * @param {string} billingCycle - Billing cycle ('monthly' or 'annual')
   * @param {number} duration - Duration in days (30 for monthly, 365 for annual)
   * @returns {Promise} Promise resolving to the API response
   */
  upgradeSubscription: async (plan, paymentMethod, paymentDetails = {}, billingCycle = 'monthly', duration = 30) => {
    const response = await api.post('/auth/subscription/upgrade', {
      plan,
      payment_method: paymentMethod,
      payment_details: paymentDetails,
      billing_cycle: billingCycle,
      duration: duration
    });
    
    // Update the user in localStorage
    if (response.data.user) {
      authService.setCurrentUser(response.data.user);
    }
    
    return response.data;
  },
  
  /**
   * Cancel user's subscription
   * @returns {Promise} Promise resolving to the API response
   */
  cancelSubscription: async () => {
    const response = await api.post('/auth/subscription/cancel');
    
    // Update the user in localStorage
    if (response.data.user) {
      authService.setCurrentUser(response.data.user);
    }
    
    return response.data;
  },
  
  /**
   * Get subscription details for the current user
   * @returns {object} Subscription details
   */
  getSubscriptionDetails: () => {
    const user = authService.getCurrentUser();
    
    if (!user) {
      return null;
    }
    
    return {
      plan: user.subscription_plan,
      isActive: user.is_subscription_active,
      startDate: user.subscription_started_at,
      expiryDate: user.subscription_expires_at,
      daysRemaining: user.subscription_time_remaining,
      billingCycle: user.billing_cycle || 'monthly'
    };
  },
  
  /**
   * Check if user has premium access
   * @returns {boolean} True if user has premium access
   */
  hasPremiumAccess: () => {
    const user = authService.getCurrentUser();
    
    // User must be logged in, have a paid plan, and subscription must be active
    return !!(user && 
      user.subscription_plan === 'standard' && 
      user.is_subscription_active);
  },
  
  /**
   * Get annual pricing for a specific plan from the API
   * @param {string} planSlug - Plan slug ('standard')
   * @returns {Promise<object>} Annual pricing details
   */
  getAnnualPricing: async (planSlug) => {
    if (!planSlug || planSlug === 'free') {
      return null;
    }
    
    try {
      const response = await api.get(`/pricing/annual/${planSlug}`);
      return response.data.annualPricing;
    } catch (error) {
      console.error(`Failed to get annual pricing for ${planSlug}:`, error);
      // Fallback to hardcoded values if API fails
      if (planSlug === 'standard') {
        return {
          price: '$300',
          period: 'per year',
          savings: 'Save $49 annually'
        };
      }

      return null;
    }
  }
};

export default subscriptionService;